import AppPromoBanner from "@/components/app-promo-banner";
import React, { useState, useEffect } from 'react';
import styles from './index.module.css';

// Define the structure of a single winner
interface Winner {
    winner_id: number;
    promotion: string;
    first_name: string;
    last_name: string;
    city: string;
    state: string;
    prize: string;
}

// Define the structure for grouped giveaways
interface GiveawayGroup {
    [key: string]: Winner[];
}

export default function AccountWinners() {
    const [giveaways, setGiveaways] = useState<GiveawayGroup>({});

    useEffect(() => {
        fetchWinners();
    }, []);

    const fetchWinners = async () => {
        const API_URL = 'https://fueling-discovery-api.burcs.workers.dev/api/previous-winners';
        const LIMIT = 50;
        let page = 0;
        let all: Winner[] = [];

        try {
            while (true) {
                const url = `${API_URL}?page=${page}&limit=${LIMIT}`;
                const response = await fetch(url, {
                    method: 'GET',
                });

                if (!response.ok) {
                    console.error('Error fetching winners:', response.status, response.statusText);
                    break;
                }

                const data: { success: boolean; response: { items: Winner[] } } = await response.json();
                const items = data?.response?.items || [];

                if (!items.length) break;

                all = all.concat(items);
                if (items.length < LIMIT) break;
                page += 1;
            }

            if (all.length) {
                processWinners(all);
            }
        } catch (error) {
            console.error('Error fetching winners:', error);
        }
    };

    const processWinners = (items: Winner[]) => {
        const grouped = items.reduce<GiveawayGroup>((acc, item) => {
            acc[item.promotion] = acc[item.promotion] || [];
            acc[item.promotion].push(item);
            return acc;
        }, {});

        setGiveaways(grouped);
    };

    return (
        <div className={styles.container}>
            <div className={styles.title}>Previous Winners</div>
            <div className={styles.subtitle}>Below are the past Kwik Fill Giveaway Winners.</div>

            {Object.entries(giveaways).map(([giveawayName, winners], index) => (
                <AccordionItem key={index} title={giveawayName} winners={winners} />
            ))}
        </div>
    );
}

interface AccordionItemProps {
    title: string;
    winners: Winner[];
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, winners }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={styles.section_container}>
            <div className={styles.row}>
                <div className={styles.giveaway_name}>{title}</div>
                <div className={styles.toggle_visibility} onClick={() => setIsOpen(!isOpen)}>
                    {isOpen ? 'Hide' : 'View'} Winners
                </div>
            </div>

            {isOpen && (
                <div className={styles.details}>
                    {winners.map((winner, index) => (
                        <div key={index} className={styles.details_list}>
                            <div className={styles.name}>{winner.first_name} {winner.last_name} ({winner.city}, {winner.state})</div>
                            <div> - {winner.prize}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
